import { useState } from 'react';
import { Award, Plus } from 'lucide-react';
import { ApiClientError } from '@techsofit/app-kit';
import { Button, Card, EmptyState, Input, Select, Skeleton, useConfirm } from '@techsofit/ui';
import { FormShell } from '../components/FormShell';
import { useAsync } from '../lib/useAsync';
import {
  createBadge,
  deleteBadge,
  getBadges,
  updateBadge,
  type AdminBadge,
  type BadgeCriteria,
  type BadgeTier,
} from '../lib/adminApi';

const TIERS: BadgeTier[] = ['BRONZE', 'SILVER', 'GOLD'];
const CRITERIA: { value: BadgeCriteria; label: string }[] = [
  { value: 'WORKOUTS_COMPLETED', label: 'Workouts completed' },
  { value: 'STREAK_DAYS', label: 'Day streak' },
  { value: 'PROGRAM_COMPLETED', label: 'Program completed' },
];
const criteriaLabel = (c: BadgeCriteria) => CRITERIA.find((x) => x.value === c)?.label ?? c;

export function BadgesPage() {
  const { data, loading, error, reload } = useAsync(getBadges);
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const list = data ?? [];

  if (adding) {
    return (
      <FormShell title="New badge" onBack={() => setAdding(false)}>
        <BadgeForm mode="create" onCancel={() => setAdding(false)} onDone={() => { setAdding(false); reload(); }} />
      </FormShell>
    );
  }
  const editing = list.find((b) => b.id === editingId);
  if (editing) {
    return (
      <FormShell title="Edit badge" onBack={() => setEditingId(null)}>
        <BadgeForm mode="edit" badge={editing} onCancel={() => setEditingId(null)} onDone={() => { setEditingId(null); reload(); }} />
      </FormShell>
    );
  }

  return (
    <section className="mt-6 animate-fade-up">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-display-md text-ink">Badges</h1>
          <p className="mt-1 text-ink-soft">Achievements members earn as they train.</p>
        </div>
        <Button onClick={() => setAdding(true)}>
          <Plus className="size-4" aria-hidden /> New badge
        </Button>
      </div>

      {loading ? (
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-24" />)}
        </div>
      ) : error ? (
        <EmptyState title="Couldn't load badges" description={error} />
      ) : list.length === 0 ? (
        <EmptyState title="No badges yet" description="Create one to start motivating members." />
      ) : (
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          {list.map((b) => (
            <BadgeCard key={b.id} b={b} onEdit={() => setEditingId(b.id)} onDeleted={reload} />
          ))}
        </div>
      )}
    </section>
  );
}

function BadgeCard({ b, onEdit, onDeleted }: { b: AdminBadge; onEdit: () => void; onDeleted: () => void }) {
  const confirm = useConfirm();
  const [busy, setBusy] = useState(false);
  async function remove() {
    if (!(await confirm({ title: `Delete "${b.title}"?`, description: 'This cannot be undone.', confirmLabel: 'Delete', tone: 'danger' }))) return;
    setBusy(true);
    try {
      await deleteBadge(b.id);
      onDeleted();
    } finally {
      setBusy(false);
    }
  }
  return (
    <Card className="flex items-center gap-3 p-4">
      <span className="grid size-11 shrink-0 place-items-center rounded-full bg-dark text-on-dark">
        <Award className="size-5" aria-hidden />
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <p className="truncate font-medium text-ink">{b.title}</p>
          {!b.published && <span className="rounded-pill bg-surface px-2 py-0.5 text-xs text-muted">Hidden</span>}
        </div>
        <p className="text-xs text-muted">
          {b.tier} · {criteriaLabel(b.criteria)} ≥ {b.threshold}
        </p>
      </div>
      <Button size="sm" variant="ghost" onClick={onEdit}>Edit</Button>
      <Button size="sm" variant="ghost" loading={busy} onClick={remove} className="text-bad-ink">Delete</Button>
    </Card>
  );
}

function BadgeForm({ mode, badge, onCancel, onDone }: { mode: 'create' | 'edit'; badge?: AdminBadge; onCancel: () => void; onDone: () => void }) {
  const [code, setCode] = useState(badge?.code ?? '');
  const [title, setTitle] = useState(badge?.title ?? '');
  const [description, setDescription] = useState(badge?.description ?? '');
  const [tier, setTier] = useState<BadgeTier>(badge?.tier ?? 'BRONZE');
  const [criteria, setCriteria] = useState<BadgeCriteria>(badge?.criteria ?? 'WORKOUTS_COMPLETED');
  const [threshold, setThreshold] = useState(String(badge?.threshold ?? 1));
  const [published, setPublished] = useState(badge?.published ?? true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    setError(null);
    if (!title.trim()) return setError('Give the badge a title.');
    if (mode === 'create' && !code.trim()) return setError('Give the badge a code (e.g. first-workout).');
    setSaving(true);
    try {
      const body = { title: title.trim(), description: description.trim() || null, tier, criteria, threshold: Number(threshold) || 1, published };
      if (mode === 'create') await createBadge({ ...body, code: code.trim() });
      else await updateBadge(badge!.id, body);
      onDone();
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'Could not save the badge.');
      setSaving(false);
    }
  }

  return (
    <Card className="flex flex-col gap-3 p-5 sm:col-span-2">
      <div className="grid gap-3 sm:grid-cols-2">
        {mode === 'create' && <Input label="Code" value={code} onChange={(e) => setCode(e.target.value)} placeholder="first-workout" />}
        <Input label="Title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="First Steps" />
        <Select label="Tier" value={tier} onChange={(e) => setTier(e.target.value as BadgeTier)}>
          {TIERS.map((t) => <option key={t} value={t}>{t}</option>)}
        </Select>
        <Select label="Earned by" value={criteria} onChange={(e) => setCriteria(e.target.value as BadgeCriteria)}>
          {CRITERIA.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
        </Select>
        <Input label="Threshold" type="number" inputMode="numeric" value={threshold} onChange={(e) => setThreshold(e.target.value)} />
      </div>
      <Input label="Description" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Complete your first workout" />
      <label className="flex items-center gap-2 text-sm text-ink">
        <input type="checkbox" checked={published} onChange={(e) => setPublished(e.target.checked)} /> Published
      </label>
      {error && <p role="alert" className="rounded-control bg-bad-soft px-3.5 py-2 text-sm font-medium text-bad-ink">{error}</p>}
      <div className="flex items-center justify-end gap-2">
        <Button size="sm" variant="ghost" onClick={onCancel} disabled={saving}>Cancel</Button>
        <Button size="sm" onClick={submit} loading={saving}>Save</Button>
      </div>
    </Card>
  );
}
